import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Button, Form, Modal } from 'react-bootstrap';

import { buildAclGroups, getCorrectEnums } from '../../../utils/Functions';
import { BrandAccessRightsService } from '../service';
import { BRAND_ACL_GROUPS, BrandAclRequest, BrandAclResponse } from '../types';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';

interface BrandAccessRightsModalProps {
    show: boolean;
    closeModal: () => void;
    selectedBrand?: BrandAclResponse;
}

const initialAccessRight: BrandAclRequest = {
    key: '',
    name: '',
    nameDe: '',
    isDefault: false,
    isEnabled: false,
    acl: [],
    desc: '',
    descDe: '',
};

function BrandAccessRightsModal({ show, closeModal, selectedBrand }: BrandAccessRightsModalProps) {
    const { BrandUserAclEnum } = useSelector((state: any) => state.enums);

    const isEditMode = !!selectedBrand?.id;

    const [accessRight, setAccessRight] = useState<BrandAclRequest>(initialAccessRight);
    const [filter, setFilter] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (show) {
            setAccessRight(
                isEditMode
                    ? {
                          key: selectedBrand?.key ?? '',
                          name: selectedBrand?.name ?? '',
                          nameDe: selectedBrand?.nameDe ?? '',
                          isDefault: selectedBrand?.isDefault ?? false,
                          isEnabled: selectedBrand?.isEnabled ?? false,
                          acl: selectedBrand?.acl ?? [],
                          desc: selectedBrand.desc ?? '',
                          descDe: selectedBrand?.descDe ?? '',
                      }
                    : initialAccessRight
            );
            setFilter('');
        }
    }, [show, selectedBrand, isEditMode]);

    const groups = useMemo(() => buildAclGroups(BRAND_ACL_GROUPS ?? [], BrandUserAclEnum), [BrandUserAclEnum]);

    const selectedCount = accessRight.acl?.length ?? 0;
    const totalCount = BrandUserAclEnum?.length ?? 0;
    const filterQuery = filter.toLowerCase().trim();

    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, checked, type } = e.target;
        setAccessRight(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;
        setAccessRight(prev => ({
            ...prev,
            acl: checked ? [...(prev.acl ?? []), value] : (prev.acl ?? []).filter(item => item !== value),
        }));
    };

    const setGroupSelection = (items: string[], select: boolean) => {
        setAccessRight(prev => {
            const current = new Set(prev.acl ?? []);
            items.forEach(i => (select ? current.add(i) : current.delete(i)));
            return { ...prev, acl: Array.from(current) };
        });
    };

    const clearAll = () => setAccessRight(prev => ({ ...prev, acl: [] }));

    const saveAccessRight = () => {
        if (!accessRight.key.trim()) {
            ErrorToast({ response: { data: { message: 'Key is required' } } });
            return;
        }

        setSaving(true);

        const request = isEditMode
            ? BrandAccessRightsService.editBrandAcl(selectedBrand!.id, accessRight)
            : BrandAccessRightsService.createBrandAcl(accessRight);

        request
            .then(() => {
                SuccessToast(isEditMode ? 'Role updated successfully' : 'Role created successfully');
                closeModal();
            })
            .catch(err => ErrorToast(err))
            .finally(() => setSaving(false));
    };

    return (
        <Modal show={show} onHide={closeModal} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {isEditMode ? `Update Brand User role — ${selectedBrand?.name}` : 'Create Brand User role'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row g-2 mb-3">
                    <Form.Group className="col-12">
                        <Form.Label>Key *</Form.Label>
                        <Form.Control
                            name="key"
                            placeholder="Unique role key"
                            value={accessRight.key}
                            onChange={handleFieldChange}
                        />
                    </Form.Group>
                </div>
                <div className="row g-2 mb-3">
                    <Form.Group className="col-6">
                        <Form.Label>Name EN</Form.Label>
                        <Form.Control
                            name="name"
                            placeholder="Role name in EN"
                            value={accessRight.name}
                            onChange={handleFieldChange}
                        />
                    </Form.Group>
                    <Form.Group className="col-6">
                        <Form.Label>Name EN</Form.Label>
                        <Form.Control
                            name="nameDe"
                            placeholder="Role name in DE"
                            value={accessRight.nameDe}
                            onChange={handleFieldChange}
                        />
                    </Form.Group>
                </div>
                <div className="row g-2 mb-3">
                    <Form.Group className="col-6">
                        <Form.Label>Role description EN</Form.Label>
                        <Form.Control
                            name="desc"
                            placeholder="Type description in EN"
                            value={accessRight.desc}
                            onChange={handleFieldChange}
                        />
                    </Form.Group>
                    <Form.Group className="col-6">
                        <Form.Label>Role description DE</Form.Label>
                        <Form.Control
                            name="descDe"
                            placeholder="Type description in DE"
                            value={accessRight.descDe}
                            onChange={handleFieldChange}
                        />
                    </Form.Group>
                </div>

                <div className="d-flex align-items-center gap-4 mb-4">
                    <Form.Check
                        type="checkbox"
                        name="isEnabled"
                        label="isActive"
                        checked={accessRight.isEnabled}
                        onChange={handleFieldChange}
                    />
                    <Form.Check
                        type="checkbox"
                        name="isDefault"
                        label="isDefault"
                        checked={accessRight.isDefault}
                        onChange={handleFieldChange}
                    />
                </div>

                <div className="d-flex align-items-center gap-2 mb-4">
                    <small className="text-muted ms-auto">
                        {selectedCount} of {totalCount} selected
                    </small>
                    <Button size="sm" variant="outline-primary" onClick={clearAll} disabled={!selectedCount}>
                        Clear all
                    </Button>
                </div>

                {groups.map(group => {
                    const visibleItems = filterQuery
                        ? group.items.filter(i => getCorrectEnums(i).toLowerCase().includes(filterQuery))
                        : group.items;

                    if (!visibleItems.length) return null;

                    const selectedInGroup = group.items.filter(i => accessRight.acl?.includes(i)).length;

                    return (
                        <section key={group.key} className="mb-4">
                            <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h6 className="mb-0">{group.label}</h6>
                                    <Badge bg="light" text="dark" className="fw-normal">
                                        {selectedInGroup}/{group.items.length}
                                    </Badge>
                                </div>
                                <div className="d-flex gap-2">
                                    {selectedInGroup < group.items.length && (
                                        <Button
                                            size="sm"
                                            variant="link"
                                            className="p-0 text-decoration-none"
                                            onClick={() => setGroupSelection(group.items, true)}
                                        >
                                            Select all
                                        </Button>
                                    )}
                                    {selectedInGroup > 0 && selectedInGroup < group.items.length && (
                                        <span className="text-muted">·</span>
                                    )}
                                    {selectedInGroup > 0 && (
                                        <Button
                                            size="sm"
                                            variant="link"
                                            className="p-0 text-decoration-none"
                                            onClick={() => setGroupSelection(group.items, false)}
                                        >
                                            Clear all
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="row g-2">
                                {visibleItems.map(item => (
                                    <Form.Group key={item} className="col-4">
                                        <Form.Check
                                            label={getCorrectEnums(item)}
                                            value={item}
                                            checked={accessRight.acl?.includes(item) ?? false}
                                            onChange={handleToggle}
                                        />
                                    </Form.Group>
                                ))}
                            </div>
                        </section>
                    );
                })}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={closeModal}>
                    Close
                </Button>
                <Button onClick={saveAccessRight} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default BrandAccessRightsModal;
